import React, { useState, useEffect, useCallback } from 'react';
import { autovoteApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

function AutovotingHistory() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  
  // States for custom input
  const [dropdownValue, setDropdownValue] = useState('20');
  const [inputLimit, setInputLimit] = useState('20');
  const [inputPage, setInputPage] = useState('1');

  useEffect(() => {
    setInputPage(page.toString());
  }, [page]);

  const fetchHistory = useCallback(async (currentPage, currentLimit) => {
    if (!user?.username) return;
    
    setLoading(true);
    const offset = (currentPage - 1) * currentLimit;

    try {
      const data = await autovoteApi.getHistory(user.username, offset, currentLimit);
      if (data.history) {
        setHistory(data.history);
        setTotalCount(data.total_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch autovote history', err);
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  const handleRetry = useCallback(async (logId) => {
    if (!user?.username) return;
    setRetryingId(logId);
    try {
      await autovoteApi.retryAutovote(user.username, logId);
      await fetchHistory(page, limit);
    } catch (err) {
      alert('Failed to retry autovote: ' + (err.response?.data?.error || err.message));
    } finally {
      setRetryingId(null);
    }
  }, [user?.username, fetchHistory, page, limit]);

  const handleRetryAll = useCallback(async () => {
    if (!user?.username) return;
    if (!window.confirm('Are you sure you want to retry all failed autovotes?')) return;
    setRetryingAll(true);
    try {
      const res = await autovoteApi.retryAllAutovotes(user.username);
      alert(res.message || 'Retry all completed');
      await fetchHistory(page, limit);
    } catch (err) {
      alert('Failed to retry all autovotes: ' + (err.response?.data?.error || err.message));
      await fetchHistory(page, limit);
    } finally {
      setRetryingAll(false);
    }
  }, [user?.username, fetchHistory, page, limit]);

  useEffect(() => {
    fetchHistory(page, limit);
  }, [fetchHistory, page, limit]);


  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const handleDropdownChange = (e) => {
    const val = e.target.value;
    setDropdownValue(val);
    
    if (val !== 'custom') {
      const newLimit = parseInt(val, 10);
      setInputLimit(val);
      if (newLimit !== limit) {
        setLimit(newLimit);
        setPage(1);
      }
    }
  };

  const handleLimitChange = (e) => {
    setInputLimit(e.target.value);
  };

  const applyLimit = () => {
    let newLimit = parseInt(inputLimit, 10);
    if (isNaN(newLimit) || newLimit < 1) newLimit = 20;
    if (newLimit > 200) newLimit = 200;
    
    setInputLimit(newLimit.toString());
    
    if (newLimit !== limit) {
      setLimit(newLimit);
      setPage(1);
    }
  };

  const handleLimitKeyDown = (e) => {
    if (e.key === 'Enter') {
      applyLimit();
    }
  };

  const handlePageChange = (e) => {
    setInputPage(e.target.value);
  };

  const applyPage = () => {
    let newPage = parseInt(inputPage, 10);
    if (isNaN(newPage) || newPage < 1) newPage = 1;
    if (newPage > totalPages) newPage = totalPages;
    
    setInputPage(newPage.toString());
    
    if (newPage !== page) {
      setPage(newPage);
    }
  };

  const handlePageKeyDown = (e) => {
    if (e.key === 'Enter') {
      applyPage();
    }
  };

  const hasFailedVotes = history.some(row => row.status === 'failed');

  const renderPaginationControls = (position) => (
    <div className="history-selector">
      <div className="history-selector-left" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span className="history-selector-label">Per page:</span>
        <select 
          value={dropdownValue} 
          onChange={handleDropdownChange}
          className="av-form-control history-selector-input"
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="15">15</option>
          <option value="20">20</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="custom">Custom</option>
        </select>

        {dropdownValue === 'custom' && (
          <input 
            type="number" 
            value={inputLimit}
            onChange={handleLimitChange}
            onKeyDown={handleLimitKeyDown}
            onBlur={applyLimit}
            className="av-form-control history-selector-input"
            min="1"
            max="200"
            placeholder="Qty"
          />
        )}
        <span className="history-selector-total">
          (Total: {totalCount})
        </span>

        {position === 'top' && hasFailedVotes && (
          <button
            className="av-btn av-btn-secondary"
            style={{
              padding: '5px 12px',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginLeft: '12px',
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)'
            }}
            disabled={retryingAll || loading}
            onClick={handleRetryAll}
            title="Retry all failed autovotes"
          >
            {retryingAll ? 'Retrying all…' : '↩ Retry All Failed'}
          </button>
        )}
      </div>

      <div className="history-selector-right">
        <button 
          className="av-btn history-page-btn" 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
        >
          Previous
        </button>
        
        <span className="history-selector-page-info">
          Page 
          <input 
            type="number" 
            value={inputPage}
            onChange={handlePageChange}
            onKeyDown={handlePageKeyDown}
            onBlur={applyPage}
            className="av-form-control history-page-input"
            min="1"
            max={totalPages}
          /> 
          of {totalPages}
        </span>
        
        <button 
          className="av-btn history-page-btn" 
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
        >
          Next
        </button>
      </div>
    </div>
  );

  if (loading && history.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading history...</div>;
  }

  return (
    <div className="av-section" style={{ padding: '0', backgroundColor: 'transparent' }}>
      
      {renderPaginationControls('top')}

      {!loading && history.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)' }}>
          No autovote history found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color)', opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', backgroundColor: 'var(--bg-primary)' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>Time</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>Post</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>Triggered By</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>Weight</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>Daily Votes</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row, index) => {
                const date = new Date(row.voted_at);
                const formattedDate = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                
                let triggeredBy = row.target_author || row.author;
                if (row.is_advanced) {
                  triggeredBy = 'Advanced Rule';
                } else if (row.target_voter) {
                  triggeredBy = `followed user: ${row.target_voter}`;
                } else if (row.target_list_name) {
                  triggeredBy = `${row.target_list_name} (${row.target_author || row.author})`;
                }

                let limitValue = 0;
                if (row.post_type === 'article') limitValue = row.article_daily_limit;
                else if (row.post_type === 'short') limitValue = row.short_post_daily_limit;
                else limitValue = row.comment_daily_limit;
                
                const allowedDisplay = limitValue > 0 ? limitValue : '∞';

                return (
                  <tr key={`${row.id}-${index}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {formattedDate}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Link to={`/@${row.author}/${row.permlink}`} style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 500 }}>
                        @{row.author}/{row.permlink.length > 20 ? row.permlink.substring(0, 20) + '...' : row.permlink}
                      </Link>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.95rem' }}>
                      {triggeredBy}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {row.status === 'skipped' || row.status === 'failed' ? (
                        <span title={row.error_message || 'Skipped'} style={{ 
                          display: 'inline-block', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          backgroundColor: 'var(--bg-tertiary)',
                          color: row.status === 'failed' ? 'var(--danger-color, #dc3545)' : 'var(--text-secondary)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          border: '1px solid var(--border-color)',
                          cursor: 'help'
                        }}>
                          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                        </span>
                      ) : (
                        <span style={{ 
                          display: 'inline-block', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          backgroundColor: 'rgba(var(--primary-color-rgb), 0.1)',
                          color: 'var(--primary-color)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          border: '1px solid rgba(var(--primary-color-rgb), 0.2)'
                        }}>
                          Success
                        </span>
                      )}
                      {row.status === 'failed' && (
                        <button
                          className="av-btn av-btn-secondary"
                          style={{ display: 'block', marginTop: '6px', padding: '3px 10px', fontSize: '0.82rem' }}
                          disabled={retryingId === row.id}
                          onClick={() => handleRetry(row.id)}
                        >
                          {retryingId === row.id ? 'Retrying…' : '↩ Retry'}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {row.status === 'failed' ? (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>—</span>
                      ) : (
                        <span style={{ 
                          display: 'inline-block', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          backgroundColor: 'var(--bg-secondary)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: row.weight > 0 ? 'var(--success-color, #28a745)' : 'inherit'
                        }}>
                          {row.weight > 0 ? `${(row.weight / 100).toFixed(2)}%` : '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                      {row.daily_vote_count} / {allowedDisplay}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && renderPaginationControls('bottom')}
    </div>
  );
}

export default AutovotingHistory;

